/**
 * InvoicesTable — sortable table displaying Zoho invoices.
 *
 * Props:
 *   invoices    {Array}    — sorted invoice data
 *   sortColumn  {string}   — current sort column key
 *   sortOrder   {string}   — 'asc' | 'desc'
 *   onSort      {function} — callback(columnKey)
 */

import SortableTh from '../../../components/v2/SortableTh.jsx';

export const INVOICE_COLUMNS = [
  { key: 'invoice#',     label: 'Invoice #',    sortable: true,  align: 'left' },
  { key: 'status',       label: 'Status',       sortable: true,  align: 'left' },
  { key: 'invoice_date', label: 'Invoice Date', sortable: true,  align: 'left' },
  { key: 'due_date',     label: 'Due Date',     sortable: true,  align: 'left' },
  { key: 'total',        label: 'Total',        sortable: true,  align: 'right' },
  { key: 'balance',      label: 'Balance',      sortable: true,  align: 'right' },
];

const STATUS_BADGE_MAP = {
  'Paid':            'bp-badge--success',
  'Sent':            'bp-badge--info',
  'Partially Paid':  'bp-badge--warning',
  'Overdue':         'bp-badge--danger',
  'Payment Pending': 'bp-badge--info',
};

const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatDate(dateStr) {
  if (!dateStr || dateStr === '0000-00-00 00:00:00') return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Sort arrow SVG */
const SortIcon = ({ direction }) => (
  <svg className="bp-table__sort-icon" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    {direction === 'asc' ? (
      <path d="M8 4l4 5H4l4-5z" />
    ) : (
      <path d="M8 12l4-5H4l4 5z" />
    )}
  </svg>
);

const InvoicesTable = ({ invoices, sortColumn, sortOrder, onSort }) => {
  const renderCell = (inv, col) => {
    const val = inv[col.key];
    switch (col.key) {
      case 'status': {
        const badgeClass = STATUS_BADGE_MAP[val] || 'bp-badge--muted';
        return (
          <span className={`bp-badge ${badgeClass}`}>
            <span className="bp-badge__dot" aria-hidden="true" />
            {val}
          </span>
        );
      }
      case 'invoice_date':
      case 'due_date':
        return formatDate(val);
      case 'total':
      case 'balance':
        return currencyFmt.format(parseFloat(val) || 0);
      default:
        return val;
    }
  };

  return (
    <div className="bp-table-wrap">
      <table className="bp-table">
        <thead className="bp-table__head">
          <tr>
            {INVOICE_COLUMNS.map((col) => {
              const isActive = sortColumn === col.key;
              const thClass = [
                'bp-table__th',
                col.sortable ? 'bp-table__th--sortable' : '',
                col.align === 'right' ? 'bp-table__th--right' : '',
                isActive ? 'bp-table__th--active' : '',
              ].filter(Boolean).join(' ');

              /* A real button in the header (keyboard, screen reader) rather than a
                 clickable <th>; aria-sort says which column is sorted (B24). */
              return col.sortable ? (
                <SortableTh
                  key={col.key}
                  sortKey={col.key}
                  sortColumn={sortColumn}
                  sortOrder={sortOrder}
                  onSort={onSort}
                  className={thClass}
                >
                  {col.label}
                  {isActive ? <SortIcon direction={sortOrder} /> : null}
                </SortableTh>
              ) : (
                <th key={col.key} className={thClass} scope="col">{col.label}</th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bp-table__body">
          {invoices.map((inv) => (
            <tr key={inv.invoice_id} className="bp-table__row">
              {INVOICE_COLUMNS.map((col) => (
                <td
                  key={col.key}
                  className={[
                    'bp-table__td',
                    col.align === 'right' ? 'bp-table__td--right' : '',
                    col.key === 'invoice#' ? 'bp-table__td--bold' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {renderCell(inv, col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InvoicesTable;
